const LITERAL_PREFIX_RE = /^[A-Za-z0-9 _-]{0,8}$/;
const MIN_PADDING_WIDTH = 1;
const MAX_PADDING_WIDTH = 6;
export const LITERAL_PREFIX_MAX_LENGTH = 8;
export const PADDING_WIDTH_MIN = MIN_PADDING_WIDTH;
export const PADDING_WIDTH_MAX = MAX_PADDING_WIDTH;
export const START_VALUE_MIN = 1;
export const START_VALUE_MAX = 999_999;
/**
 * DATEV's Belegfeld 1 (the field that carries the Rechnungsnummer in an EXTF
 * Buchungsstapel) is capped at 36 characters. Any composed Rechnungsnummer
 * shipped to a Steuerberater must fit. The structural maxes above keep us
 * well below this — at the upper bounds we hit ~20 chars — but the format
 * constructor enforces the invariant explicitly so a future bump of any
 * structural max cannot silently produce un-exportable numbers.
 */
export const DATEV_BELEGFELD_1_MAX_LENGTH = 36;
/**
 * Structured invoice-number format used by every office to compose its
 * Rechnung / Stornorechnung / Mahnung numbers (e.g. `RE-2026-0001`).
 *
 * `compose(sequenceNumber, year)` is pure — given the same inputs and format
 * it always returns the same string. Consumed by the backend issuance handlers
 * and by the frontend foldout for its live preview.
 */
export class InvoiceNumberFormat {
    literalPrefix;
    yearFormat;
    paddingWidth;
    separator;
    constructor(props) {
        this.literalPrefix = props.literalPrefix;
        this.yearFormat = props.yearFormat;
        this.paddingWidth = props.paddingWidth;
        this.separator = props.separator;
    }
    static create(props) {
        if (!LITERAL_PREFIX_RE.test(props.literalPrefix)) {
            throw new Error(`Invalid literalPrefix "${props.literalPrefix}": must be 0–8 chars of [A-Za-z0-9 _-]`);
        }
        if (!Number.isInteger(props.paddingWidth) ||
            props.paddingWidth < MIN_PADDING_WIDTH ||
            props.paddingWidth > MAX_PADDING_WIDTH) {
            throw new Error(`Invalid paddingWidth ${props.paddingWidth}: must be an integer in ` +
                `[${MIN_PADDING_WIDTH}, ${MAX_PADDING_WIDTH}]`);
        }
        if (!['NONE', 'YY', 'YYYY'].includes(props.yearFormat)) {
            throw new Error(`Invalid yearFormat "${props.yearFormat}"`);
        }
        if (!['', '-', '/', '.'].includes(props.separator)) {
            throw new Error(`Invalid separator "${props.separator}"`);
        }
        const format = new InvoiceNumberFormat(props);
        // Worst-case Belegnummer (max sequence + max-width year) must fit DATEV's
        // 36-char Belegfeld 1. With current structural maxes we sit ~20 chars,
        // but the check guards against future-widening surprises.
        const maxYear = props.yearFormat === 'NONE' ? null : 9999;
        const worstCase = format.compose(START_VALUE_MAX, maxYear);
        if (worstCase.length > DATEV_BELEGFELD_1_MAX_LENGTH) {
            throw new Error(`Composed Rechnungsnummer "${worstCase}" exceeds DATEV Belegfeld 1 ` +
                `limit of ${DATEV_BELEGFELD_1_MAX_LENGTH} chars`);
        }
        return format;
    }
    /**
     * Composes the formatted number string for a given sequence number and year.
     *
     * - When `yearFormat === 'NONE'`, the `year` argument is ignored and may be
     *   `null`.
     * - When `yearFormat !== 'NONE'`, a numeric `year` is required.
     * - If the sequence number's digit count exceeds `paddingWidth`, the number
     *   is rendered at its natural width (no truncation).
     */
    compose(sequenceNumber, year) {
        if (!Number.isInteger(sequenceNumber) || sequenceNumber < 1) {
            throw new Error(`sequenceNumber must be a positive integer (got ${sequenceNumber})`);
        }
        const padded = String(sequenceNumber).padStart(this.paddingWidth, '0');
        const yearPart = this.formatYear(year);
        const parts = [this.literalPrefix, yearPart, padded].filter((p) => p !== '');
        return parts.join(this.separator);
    }
    /**
     * Same as `compose`, but returns `null` when validation would have thrown.
     * Used by the frontend live preview where invalid form state should render
     * an empty preview rather than crash.
     */
    tryCompose(sequenceNumber, year) {
        try {
            return this.compose(sequenceNumber, year);
        }
        catch {
            return null;
        }
    }
    /** Returns true iff `literalPrefix` is in the allowed shape. */
    static isValidLiteralPrefix(literalPrefix) {
        return LITERAL_PREFIX_RE.test(literalPrefix);
    }
    formatYear(year) {
        switch (this.yearFormat) {
            case 'NONE':
                return '';
            case 'YYYY':
                if (year === null) {
                    throw new Error('year is required when yearFormat is YYYY');
                }
                return String(year);
            case 'YY':
                if (year === null) {
                    throw new Error('year is required when yearFormat is YY');
                }
                return String(year).slice(-2).padStart(2, '0');
        }
    }
}
//# sourceMappingURL=invoice-number-format.js.map